// Tableau des lignes d'un budget (lot L4-B) : par section et par sens, code, libellé et les cinq
// montants calculés par la plateforme (prévu, engagé, liquidé, ordonnancé, payé), décisions
// modificatives et disponible ; synthèse des totaux par section.
import { SECTIONS_BUDGET, SENS_LIGNE_BUDGET, type Budget, type LigneBudgetaire, type SectionBudget, type SensLigneBudget } from '@cc/contrats';
import { Tableau, type ColonneTableau } from '@cc/ui';
import { francs, grouperLignes, tauxExecution } from './logique';
import styles from './budget.module.css';

export function colonnesLignes(fictif: boolean): ColonneTableau<LigneBudgetaire>[] {
  const m = (cle: keyof LigneBudgetaire, entete: string): ColonneTableau<LigneBudgetaire> => ({ id: String(cle), entete, numerique: true, cellule: (l) => francs(l[cle] as number, fictif), valeurTri: (l) => l[cle] as number });
  return [
    { id: 'code', entete: 'Code', enTeteLigne: true, masquable: false, cellule: (l) => <span className="cc-mono">{l.code}</span>, valeurTri: (l) => l.code },
    { id: 'libelle', entete: 'Libellé', cellule: (l) => l.libelle },
    m('prevu', 'Prévu'),
    m('mouvements', 'Décisions modificatives'),
    m('engage', 'Engagé'),
    m('liquide', 'Liquidé'),
    m('ordonnance', 'Ordonnancé'),
    m('paye', 'Payé'),
    { id: 'disponible', entete: 'Disponible', numerique: true, cellule: (l) => <span className={l.disponible < 0 ? styles.negatif : undefined}>{francs(l.disponible, fictif)}</span>, valeurTri: (l) => l.disponible },
  ];
}

export function TableauLignes({ lignes, fictif }: { lignes: LigneBudgetaire[]; fictif: boolean }) {
  const colonnes = colonnesLignes(fictif);
  return (
    <>
      {grouperLignes(lignes).map((g) => (
        <section key={`${g.section}-${g.sens}`} className={styles.groupe}>
          <h3>{g.titre}</h3>
          <Tableau legende={g.titre} colonnes={colonnes} lignes={g.lignes} cleLigne={(l) => l.id} cleColonnes={`budget-lignes-${g.sens}`} />
        </section>
      ))}
    </>
  );
}

export function SyntheseBudget({ budget }: { budget: Budget }) {
  return (
    <dl className={styles.synthese} aria-label="Synthèse par section">
      {(['fonctionnement', 'investissement'] as SectionBudget[]).flatMap((s) =>
        (['depense', 'recette'] as SensLigneBudget[]).map((sens) => {
          const t = budget.totaux[s][sens];
          const taux = sens === 'depense' ? tauxExecution(t) : null;
          return (
            <div key={`${s}-${sens}`} className={styles.carte}>
              <dt>{SECTIONS_BUDGET[s]} · {SENS_LIGNE_BUDGET[sens]}</dt>
              <dd>
                <span className={styles.montant}>{francs(t.credits_ouverts, budget.fictif)}</span>
                <span className={styles.detail}>crédits ouverts{sens === 'depense' ? ` · engagé ${francs(t.engage, budget.fictif)} · payé ${francs(t.paye, budget.fictif)}` : ''}</span>
                {taux !== null ? <span className={styles.detail}>Taux d&apos;exécution : {taux.toLocaleString('fr-FR')} %</span> : null}
              </dd>
            </div>
          );
        }),
      )}
    </dl>
  );
}
